import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useAppStore } from '@/lib/store';
import { articlesApi, placesApi } from '@/lib/api';
import { Heart } from 'lucide-react';
import Colors from '@/constants/colors';
import { getTranslation } from '@/constants/translations';
import { useNavigate } from 'react-router-dom';
import PageContainer from '@/components/PageContainer';
import PageHeader from '@/components/PageHeader';
import ContentSection from '@/components/ContentSection';
import CompactCard from '@/components/CompactCard';
import { extractPlainText } from '@/lib/lexical-parser';

type FilterType = null | 'articles' | 'places';

export default function Favorites() {
  const { language } = useAppLanguage();
  const favorites = useAppStore((state) => state.favorites);
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState<FilterType>(null);

  const articlesQuery = useQuery({
    queryKey: ['articles', language],
    queryFn: () => articlesApi.getAll(language),
  });
  const placesQuery = useQuery({
    queryKey: ['places', language],
    queryFn: () => placesApi.getAll(language),
  });

  const articles = Array.isArray(articlesQuery.data) ? articlesQuery.data : [];
  const places = Array.isArray(placesQuery.data) ? placesQuery.data : [];

  const favoriteArticles = articles.filter((article) => favorites.has(article.id));
  const favoritePlaces = places.filter((place) => favorites.has(place.id));

  const filteredItems = (() => {
    if (selectedFilter === 'articles') {
      return favoriteArticles;
    } else if (selectedFilter === 'places') {
      return favoritePlaces;
    }
    return [...favoriteArticles, ...favoritePlaces];
  })();

  const getArticleTitle = (article: { title: string; title_ru?: string }) => {
    return language === 'ru' ? article.title_ru || article.title : article.title;
  };

  const getArticleSubtitle = (article: { subtitle?: string; subtitle_ru?: string }) => {
    return language === 'ru' ? article.subtitle_ru || article.subtitle : article.subtitle;
  };

  const getArticleContent = (article: { content?: string; content_ru?: string; summary?: string }) => {
    return language === 'ru' ? article.content_ru || article.content : article.content || article.summary;
  };

  const getPlaceName = (place: { name: string; name_ru?: string }) => {
    return language === 'ru' ? place.name_ru || place.name : place.name;
  };

  const getPlaceDescription = (place: { description?: string; description_ru?: string; summary?: string }) => {
    return language === 'ru' ? place.description_ru || place.description : place.description || place.summary;
  };

  return (
    <PageContainer>
      <PageHeader title={getTranslation('favorites', language)} />

      {/* Filter Chips */}
      {(favoriteArticles.length > 0 || favoritePlaces.length > 0) && (
        <ContentSection style={{ ...styles.filtersContainer, padding: '0 24px' }}>
          <div style={styles.filterScroll}>
            <button
              style={{
                ...styles.filterChip,
                ...(selectedFilter === null ? styles.filterChipActive : {}),
              }}
              onClick={() => setSelectedFilter(null)}
            >
              <span style={selectedFilter === null ? styles.filterTextActive : styles.filterText}>
                {getTranslation('all', language)}
              </span>
            </button>
            {favoriteArticles.length > 0 && (
              <button
                style={{
                  ...styles.filterChip,
                  ...(selectedFilter === 'articles' ? styles.filterChipActive : {}),
                }}
                onClick={() => setSelectedFilter('articles')}
              >
                <span
                  style={
                    selectedFilter === 'articles' ? styles.filterTextActive : styles.filterText
                  }
                >
                  {getTranslation('articles', language)}
                </span>
              </button>
            )}
            {favoritePlaces.length > 0 && (
              <button
                style={{
                  ...styles.filterChip,
                  ...(selectedFilter === 'places' ? styles.filterChipActive : {}),
                }}
                onClick={() => setSelectedFilter('places')}
              >
                <span
                  style={
                    selectedFilter === 'places' ? styles.filterTextActive : styles.filterText
                  }
                >
                  {getTranslation('places', language)}
                </span>
              </button>
            )}
          </div>
        </ContentSection>
      )}

      {filteredItems.length > 0 ? (
        <ContentSection style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0 24px' }}>
          {filteredItems.map((item) => {
            const isArticle = 'title' in item;
            const path = isArticle ? `/articles/${item.id}` : `/places/${item.id}`;

            return (
              <CompactCard
                key={item.id}
                id={item.id}
                imageUrl={(item as { image_url?: string; imageUrl: string }).image_url || item.imageUrl}
                imageAlt={isArticle ? getArticleTitle(item as { title: string; title_ru?: string }) : getPlaceName(item as { name: string; name_ru?: string })}
                title={isArticle ? getArticleTitle(item as { title: string; title_ru?: string }) : getPlaceName(item as { name: string; name_ru?: string })}
                subtitle={isArticle ? getArticleSubtitle(item as { subtitle?: string; subtitle_ru?: string }) : undefined}
                description={
                  isArticle
                    ? extractPlainText(getArticleContent(item as { content?: string; content_ru?: string; summary?: string }) || '').substring(0, 80) + '...' || 'No description'
                    : extractPlainText(getPlaceDescription(item as { description?: string; description_ru?: string; summary?: string }) || '').substring(0, 80) + '...' || 'No description'
                }
                location={isArticle ? undefined : (item as { location?: string }).location}
                promoted={isArticle ? !!(item as { featured?: boolean }).featured : !!(item as { promoted?: boolean }).promoted}
                onCardClick={() => navigate(path, { state: { fromApp: true } })}
              />
            );
          })}
        </ContentSection>
      ) : (
        <ContentSection>
          <div style={styles.emptyFavoritesState}>
            <Heart size={64} color={Colors.border} />
            <h3 style={styles.emptyFavoritesTitle}>
              {selectedFilter
                ? getTranslation('noFavorites', language)
                : getTranslation('noFavorites', language)}
            </h3>
            <p style={styles.emptyFavoritesText}>
              {language === 'ru'
                ? 'Нажмите на значок сердца на статьях и местах, чтобы сохранить их здесь'
                : language === 'pl'
                ? 'Dotknij ikony serca na artykułach i miejscach, aby zapisać je tutaj'
                : 'Tap the heart icon on articles and places to save them here'}
            </p>
          </div>
        </ContentSection>
      )}

      <div style={{ height: '40px' }} />
    </PageContainer>
  );
}

const styles = {
  emptyFavoritesState: {
    padding: '60px 24px',
    textAlign: 'center' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '16px',
  } as React.CSSProperties,

  emptyFavoritesTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: Colors.text,
    margin: 0,
  } as React.CSSProperties,

  emptyFavoritesText: {
    fontSize: '14px',
    color: Colors.textSecondary,
    margin: 0,
    maxWidth: '320px',
    lineHeight: '22px',
  } as React.CSSProperties,

  filtersContainer: {
    marginTop: '8px',
    marginBottom: '24px',
    padding: '0 !important',
  } as React.CSSProperties,

  filterScroll: {
    display: 'flex',
    gap: '12px',
    overflowX: 'auto' as const,
    scrollBehavior: 'smooth' as const,
    justifyContent: 'flex-start',
  } as React.CSSProperties,

  filterChip: {
    padding: '8px 16px',
    borderRadius: '20px',
    backgroundColor: Colors.surface,
    border: `1px solid ${Colors.border}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    fontWeight: '600',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  } as React.CSSProperties,

  filterChipActive: {
    backgroundColor: Colors.primary,
    borderColor: Colors.primary,
  } as React.CSSProperties,

  filterText: {
    color: Colors.text,
  } as React.CSSProperties,

  filterTextActive: {
    color: Colors.surface,
  } as React.CSSProperties,
};
